/**
 * STORY-337: a long list answered with a paged table also carries, in its
 * text, a preview of the table's first rows — for every reader that gets the
 * text only (the CLI, the SSE stream, A2A, and the chat history the model's
 * follow-up context is rebuilt from). The web UI renders the table itself, so
 * showing that preview too would print the first rows twice.
 *
 * The backend names the block exactly: `table_data.text_preview` is a verbatim
 * substring of the message content. Only that block is dropped from what is
 * rendered — headings, totals, notices and anything else stay — and the
 * stored content is never changed (copy, history and the model's context keep
 * the full text).
 *
 * Fails open: with no preview, or one the content does not contain (a message
 * saved before STORY-337, or a content rewrite the preview missed), the
 * content is rendered in full. Rows twice beats rows lost.
 */
export interface WithTextPreview {
  text_preview?: string | null;
}

export function withoutTablePreview(
  content: string,
  tableData?: WithTextPreview | null,
): string {
  return withoutPreview(content, tableData?.text_preview);
}

function withoutPreview(content: string, preview: unknown): string {
  if (typeof preview !== 'string' || preview.length === 0) return content;
  const at = content.indexOf(preview);
  if (at < 0) return content;
  const before = content.slice(0, at).trimEnd();
  const after = content.slice(at + preview.length).trimStart();
  if (!before) return after;
  if (!after) return before;
  // The block stood between two paragraphs; they stay two paragraphs.
  return `${before}\n\n${after}`;
}

/**
 * STORY-298: the same, for every named preview an answer carries — the
 * table's and each chart's (a chart's `text_preview` is the fenced block of
 * block glyphs that the interactive chart replaces). Each block is dropped
 * on its own terms: one that is missing or not found leaves the content as
 * it is and the others are still dropped. Fails open, like the table's.
 */
export function withoutPreviews(
  content: string,
  holders: ReadonlyArray<WithTextPreview | null | undefined>,
): string {
  let out = content;
  for (const holder of holders) out = withoutPreview(out, holder?.text_preview);
  return out;
}
